import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface GetClassHistoryByStudentIdResponse {
  items: [StudentHistory];
  pagination: Pagination;
}

export interface StudentHistory {
  _id: string;
  studentId: string;
  studioId: string;
  parentId: string;
  enrollments: Array<{
    enrollmentId: {
      _id: string;
      title: string;
      startDate: string; // date string
      endDate: string; // date string
      tuitionFee: number;
      /** Null when the class's session was deleted or never linked. */
      session: {
        _id: string;
        name: string;
      } | null;
    } | null;
    status: 'inactive' | 'term-ended' | 'class-transferred'; // 'active'
    createdAt: string; // date string
    updatedAt: string; // date string
    droppingReason?: string | null;
    scheduledDropDate?: string | null;
  }>;
  /** Event enrollment activity (camps, pop-ups, workshops) on the same record. */
  events?: Array<{
    eventId: {
      _id: string;
      title: string;
      startDate: string; // date string
      endDate: string; // date string
      tuitionFee: number;
      /** Null when the event's session was deleted or never linked. */
      session: {
        _id: string;
        name: string;
      } | null;
    } | null;
    status: string; // e.g. 'active' | 'dropped' | 'cancelled' | 'term_ended'
    createdAt: string; // date string
    updatedAt: string; // date string
    droppingReason?: string | null;
    dropDate?: string | null;
  }>;
  createdAt: string; // date string
  updatedAt: string; // date string
  __v: number;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export const getClassHistoryByStudentId = async ({
  studentId,
  ...params
}: GetClassHistoryByStudentIdParams) => {
  const response = await baseAPI.get<GetClassHistoryByStudentIdResponse>(
    `/enrollment-history/student/${studentId}`,
    {
      params: {
        ...params,
        sortBy: params.sortBy ?? 'createdAt',
        sortOrder: params.sortOrder ?? 'desc',
      },
    },
  );
  return response.data;
};

export interface GetClassHistoryByStudentIdParams {
  studentId: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export const getClassHistoryByStudentIdQueryOptions = (
  params: GetClassHistoryByStudentIdParams,
) =>
  queryOptions({
    queryKey: [
      KEYS.STUDENT_CLASS_HISTORY,
      params.studentId,
      {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
    ],
    queryFn: () => getClassHistoryByStudentId(params),
    placeholderData: keepPreviousData,
  });

export const useGetClassHistoryByStudentId = (
  params: GetClassHistoryByStudentIdParams,
) => {
  return useQuery(getClassHistoryByStudentIdQueryOptions(params));
};
